import React from 'react';
import type { Usuario } from '../../../types/models';
import { ListAvatar } from '../../../components/admin/list-avatar';
import { formatCargo } from '../Equipe';

interface EquipeCardViewProps {
  equipeFiltrada: Usuario[];
  statusBadge: (u: Usuario) => React.ReactNode;
  resolveRole: (u: Usuario) => string;
  renderRowActions: (u: Usuario) => React.ReactNode;
}

export function EquipeCardView({
  equipeFiltrada,
  statusBadge,
  resolveRole,
  renderRowActions,
}: EquipeCardViewProps) {
  if (equipeFiltrada.length === 0) {
    return <div className="p-4 text-center text-slate-400 text-xs">Nenhum registro encontrado.</div>;
  }

  return (
    <>
      {/* ───────────────────────────────────────────────────────────
          1. VISUALIZAÇÃO MOBILE COMPACTA (md:hidden)
          ─────────────────────────────────────────────────────────── */}
      <div className="flex md:hidden flex-col gap-2 p-2">
        {equipeFiltrada.map((u) => (
          <div
            key={u.id}
            className="border border-[var(--border-color,#e5e7eb)] rounded-xl p-2.5 bg-[var(--bg-card,white)] flex items-center gap-3 shadow-sm hover:shadow transition-shadow"
          >
            <div className="shrink-0">
              <ListAvatar
                fotoUrl={(u as any).fotoUrl}
                nome={u.nome || 'Membro'}
                role={u.cargo || resolveRole(u)}
                size={44}
              />
            </div>

            <div className="flex-1 min-w-0 flex flex-col gap-1">
              <div className="flex items-center justify-between gap-1.5">
                <span className="font-bold text-xs text-[var(--text-main,#1e293b)] truncate">
                  {u.nome}
                </span>
                <div className="shrink-0 scale-90 origin-right">
                  {statusBadge(u)}
                </div>
              </div>

              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-[10px] font-semibold text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-md">
                  {formatCargo(u.cargo, resolveRole(u))}
                </span>
              </div>
            </div>

            <div className="shrink-0 flex items-center">
              {renderRowActions(u)}
            </div>
          </div>
        ))}
      </div>

      {/* ───────────────────────────────────────────────────────────
          2. VISUALIZAÇÃO DESKTOP ORIGINAL (hidden md:grid)
          ─────────────────────────────────────────────────────────── */}
      <div className="hidden md:grid grid-cols-[repeat(auto-fill,minmax(200px,240px))] gap-4 p-3">
        {equipeFiltrada.map((u) => (
          <div
            key={u.id}
            style={{
              border: '1px solid var(--border-color, #e5e7eb)',
              borderRadius: 12,
              padding: '20px 16px',
              background: '#f8fafc',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              position: 'relative',
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
              gap: 12,
            }}
          >
            {/* Cargo floating top-left */}
            <span
              title="Cargo"
              style={{
                position: 'absolute',
                top: 12,
                left: 12,
                fontSize: '.72rem',
                fontWeight: 600,
                color: '#4b5563',
                background: '#e5e7eb',
                padding: '2px 8px',
                borderRadius: 6,
              }}
            >
              {formatCargo(u.cargo, resolveRole(u))}
            </span>

            {/* Status Badge floating top-right */}
            <div style={{ position: 'absolute', top: 12, right: 12 }}>
              {statusBadge(u)}
            </div>

            {/* Avatar on top */}
            <div style={{ marginTop: 12 }}>
              <ListAvatar
                fotoUrl={(u as any).fotoUrl}
                nome={u.nome || 'Membro'}
                role={u.cargo || resolveRole(u)}
                size={120}
              />
            </div>

            {/* Data underneath */}
            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
              <div
                style={{
                  fontWeight: 700,
                  fontSize: '.95rem',
                  color: 'var(--text-main)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {u.nome}
              </div>
            </div>

            {/* Actions at the bottom */}
            <div
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                borderTop: '1px solid var(--border-color, #e5e7eb)',
                paddingTop: 10,
                marginTop: 'auto',
              }}
            >
              {renderRowActions(u)}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
